import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { TopAd } from 'googlead/components/TopAd';
import {
  GetServerSideProps, GetServerSidePropsContext,
  GetServerSidePropsResult,
} from 'next';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { HeadMeta } from 'components/common/HeadMeta';
import { HeaderNav, HeaderNavCurLink } from 'components/common/HeaderNav';
import { Layout } from 'components/resort/Layout/Layout';
import { Main } from 'components/resort/Layout/Main';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { Footer } from 'components/blocks/Footer';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchBackgroundImages,
  fetchNearbyResorts, fetchGuideHomeFeatured,
} from 'api/home/static';
import {
  IBackgroundImages,
} from 'api/home/types';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BlockContent } from 'components/blocks/BlockContent';
import { useQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import IntegrityError from 'page-components/400';
import { usePosition } from 'util/usePosition';
import { IRegionFeaturedResort } from 'api/region/types';
import { SpinnerCenteredWrapper } from 'components/Spinner/SpinnerCenteredWrapper';
import { NearbyResortsBlock } from 'components/home/NearbyResorts';
import { NearbyLocationInput } from 'components/home/NearbyResorts/components/NearbyLocationInput';
import { useURILocation } from 'util/useLocationFromURL';
import { HeroNav } from 'components/common/HeroNav';

export type NearbyResortsProps = {
  backgroundImage: IBackgroundImages;
  featured: IRegionFeaturedResort;
  predefined: {
    lat: number,
    lng: number,
    place: string
  } | null
} & RouteAndAnalyticsProps;

export const TEMPLATE_ID = '1.3';
export const TEMPLATE_NAME = 'Nearby Resorts';

export default function NearbyResorts({
  backgroundImage,
  featured,
  predefined,
}: NearbyResortsProps) {
  const { t } = useOTSTranslation('common');
  const { t: seoT } = useOTSTranslation('seo');
  const { coords, isLoading: isPositionLoading, error: positionError } = usePosition();

  const { place: URIplace, updateURI } = useURILocation(predefined);

  const [currentPos, setCurrentPos] = useState<{ latitude: number, longitude: number, title: string } | null>(URIplace ?? null);
  const isCustomPlace = !!currentPos?.title;

  if (!featured || !backgroundImage) {
    return <IntegrityError />;
  }

  const { current: curNearby, isLoading } = useQueryWithInitialValue(
    ['fetchNearbyResorts', currentPos?.latitude, currentPos?.longitude],
    async () => fetchNearbyResorts(currentPos?.latitude, currentPos?.longitude),
    [],
    { keepPreviousData: true },
  );

  useEffect(() => {
    if (!currentPos && coords) {
      setCurrentPos({ latitude: coords.latitude, longitude: coords.longitude, title: '' });
    }
  }, [coords, currentPos, isCustomPlace]);

  const isNearbyEmpty = !curNearby || curNearby.length === 0;
  const showGlobalLoading = (isPositionLoading || isLoading) && isNearbyEmpty;

  const handleLocationChange = useCallback((location?: { latitude: number, longitude: number, title: string }) => {
    setCurrentPos(location ?? null);

    if (location) {
      updateURI(`lat=${location.latitude}&lng=${location.longitude}&place=${encodeURIComponent(location.title)}`);
    } else {
      updateURI('');
    }
  }, []);

  return (
    <AdUnitContext
      adType="home"
      adTKey="other travel"
      meta={{
        Fs: undefined,
        Rs: undefined,
        Th: undefined,
        Tl: undefined,
        snow: undefined,
        Wc: undefined,
      }}
    >
      <HeadMeta
        title={currentPos?.title
          ? t('nearbyResorts.location', { location: currentPos?.title }) // FIXME: should be as seo
          : seoT('Other.nearby_resorts.title')}
        description={seoT('Other.nearby_resorts.description')}
        image={backgroundImage.image}
        keepQuery
      />
      <HeaderNav current={HeaderNavCurLink.TRIP_PLANNING} />
      <TopAd id="ski-resorts" />
      <div className="container-xl content-container">
        <HeroNav
          title={currentPos?.title
            ? t('nearbyResorts.heroTitleLoc', { location: currentPos?.title })
            : t('nearbyResorts.heroTitle')}
          backgroundPhoto={backgroundImage.image}
        />
        <Layout className="layout-container">
          <Main>
            <BoxWithShadow>
              <BlockHeader title={currentPos?.title
                ? t('nearbyResorts.seoTitleLoc', { location: currentPos?.title })
                : t('nearbyResorts.seoTitle')}
              />
              <BlockContent>
                {t('nearbyResorts.seoText')}
              </BlockContent>
            </BoxWithShadow>
            <NearbyLocationInput
              isCustom={isCustomPlace}
              positionError={!!positionError}
              setLocationCoords={handleLocationChange}
              initialLocationTitle={currentPos?.title}
            />
            {showGlobalLoading && <SpinnerCenteredWrapper />}
            {curNearby && <NearbyResortsBlock resorts={curNearby} from={currentPos} isFresh={!isLoading} />}
          </Main>
        </Layout>
      </div>
      <BottomMountains />
      <Footer />
    </AdUnitContext>
  );
}

export const fetchProps = async (context: GetServerSidePropsContext<{}>): Promise<NearbyResortsProps> => {
  const lat = parseFloat(String(context.query?.lat ?? '0'));
  const lng = parseFloat(String(context.query?.lng ?? '0'));
  const place = String(context.query?.place ?? '');
  let predefined: { lat: number, lng: number, place: string } | null = null;

  if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat && lng && place) {
    predefined = { lat, lng, place };
  }

  const featured = await fetchGuideHomeFeatured();
  const backgroundImage = await fetchBackgroundImages();

  const f: NearbyResortsProps = {
    type: 'global',
    route: 'nearby-resorts',
    featured,
    backgroundImage,
    predefined,
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
  };
  return f;
};

export const getNearbyResortsServerSideProps: GetServerSideProps<NearbyResortsProps> = async (context: GetServerSidePropsContext<{}>) => {
  const f: GetServerSidePropsResult<NearbyResortsProps> = {
    props: await fetchProps(context),
  };
  return f;
};
